import { FaEyeSlash, FaGlobe, FaLock } from "react-icons/fa";
import { UPLOAD_URL } from "../../../utils/constants";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { clearDashboard } from "../../../store/dashboard.slice";
import { putRequest } from "../../../helpers/api.helper";
import { toast } from "react-toastify";
import { convertStringToBoolean } from "../../../helpers/function.helper";
import {
  getPrivacyOverlayLabel,
  shouldBlurProfileImage,
} from "../../../utils/imagePrivacy";

type PrivacyModalProps = {
  closePrivacyModal: () => void;
  profile: any;
  setProfile: (profile: any) => void;
};

const PrivacyModal = ({
  closePrivacyModal,
  profile,
  setProfile,
}: PrivacyModalProps) => {
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const dispatch = useDispatch();
  const isPublic = convertStringToBoolean(profile.visibleToAll);

  const setVisibility = async (isPublic: boolean) => {
    if (visibilityLoading) return;
    setVisibilityLoading(true);
    try {
      const response = await putRequest(`/bureau/profile/${encodeURIComponent(profile.martialId)}/visibility`, { isPublic });
      if (response.success) {
        setProfile((previous: any) => ({ ...previous, visibleToAll: String(isPublic), contactPrivacy: response.data.contactPrivacy }));
        dispatch(clearDashboard());
        toast.success(isPublic ? "Profile is now Public." : "Profile is now Private. Other users cannot view its contact details.");
      } else toast.error(response.message || "Failed to update visibility");
    } catch {
      toast.error("Unable to update profile visibility.");
    } finally {
      setVisibilityLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <FaLock className="mr-2 text-blue-600" />
            Profile Privacy
          </h2>
          <button
            onClick={closePrivacyModal}
            className="text-gray-500 hover:text-gray-700 text-xl"
          >
            ×
          </button>
        </div>

        <div className="mb-4">
          <div className="flex items-center mb-2">
            <div className="relative w-12 h-12 mr-3">
              <img
                src={
                  profile.image
                    ? `${UPLOAD_URL}/users/profile_pic/${profile.image}`
                    : "https://www.pngitem.com/pimgs/m/581-5813504_avatar-dummy-png-transparent-png.png"
                }
                alt="Profile"
                className={`w-full h-full object-cover rounded-full ${
                  shouldBlurProfileImage(profile.imagePrivacy)
                    ? "blur-lg scale-110 brightness-90"
                    : ""
                }`}
              />
              {shouldBlurProfileImage(profile.imagePrivacy) && (
                <div className="absolute inset-0 bg-black/50 rounded-full text-white text-[8px] font-semibold tracking-wide uppercase flex flex-col items-center justify-center text-center pointer-events-none px-1">
                  <span>{getPrivacyOverlayLabel(profile.imagePrivacy)}</span>
                  <span className="text-[7px] opacity-80">Hidden</span>
                </div>
              )}
            </div>
            <div>
              <h3 className="font-semibold">{profile.fullName}</h3>
              <p className="text-sm text-gray-600">ID: {profile.martialId}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4 mb-6">
          <h4 className="font-semibold mb-3 text-center">Profile Visibility</h4>

          <div className="space-y-3">
            {/* Visible Option */}
            <div
              onClick={() =>
                setVisibility(true)
              }
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                isPublic
                  ? "border-green-500 bg-green-50 shadow-md"
                  : "border-gray-200 bg-gray-50 hover:border-green-300"
              }`}
            >
              <div className="flex items-center">
                <div
                  className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                    isPublic
                      ? "border-green-500 bg-green-500"
                      : "border-gray-300"
                  }`}
                >
                  {isPublic && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <FaGlobe
                  className={`mr-3 ${
                    isPublic ? "text-green-600" : "text-gray-400"
                  }`}
                  size={18}
                />
                <div>
                  <p className="font-medium">Public</p>
                  <p className="text-sm text-gray-600">
                    Profile is visible to all users
                  </p>
                </div>
              </div>
            </div>

            {/* Not Visible Option */}
            <div
              onClick={() =>
                setVisibility(false)
              }
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                !isPublic
                  ? "border-red-500 bg-red-50 shadow-md"
                  : "border-gray-200 bg-gray-50 hover:border-red-300"
              }`}
            >
              <div className="flex items-center">
                <div
                  className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                    !isPublic
                      ? "border-red-500 bg-red-500"
                      : "border-gray-300"
                  }`}
                >
                  {!isPublic && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <FaEyeSlash
                  className={`mr-3 ${
                    !isPublic ? "text-red-600" : "text-gray-400"
                  }`}
                  size={18}
                />
                <div>
                  <p className="font-medium">Private</p>
                  <p className="text-sm text-gray-600">
                    Profile is hidden from public search
                  </p>
                </div>
              </div>
            </div>
          </div>

          {visibilityLoading && (
            <div className="text-center mt-4">
              <div className="inline-flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm text-gray-600">Updating...</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex space-x-3">
          <button
            onClick={closePrivacyModal}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyModal;
